import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import { Button } from '~/ui/core/button/Button';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import { useFormattedVersions } from '../hooks/use-formatted-versions';

export const ComparisonLevelHeader: FunctionComponent<{
    readonly level: JobLevelDetails;
    readonly isFetching: boolean;
    readonly title: string;
    readonly versions: JobProfileVersions;
    readonly onOpen: () => void;
    readonly onSelectVersion: (version: string) => void;
}> = ({ isFetching, level, onOpen, onSelectVersion, title, versions }) => {
    const translate = useTranslate();
    const { formattedVersions } = useFormattedVersions(versions);
    const selectedVersionId = level.versionInfo?.version.toString();
    const currentVersion = formattedVersions.find((version) => version.id === selectedVersionId);

    return (
        <div
            className="flex min-w-0 w-full flex-col items-start gap-xxs py-xxs"
            data-testid={`comparison-level-header-${level.id}`}
            aria-busy={isFetching}
        >
            <Button className="min-h-10 !justify-start !px-0 text-left" variant="link" onClick={onOpen}>
                <span className="line-clamp-2 text-left text-wrap-balance">{title}</span>
            </Button>
            <div className="flex min-w-0 w-full items-center justify-between gap-s">
                <div className="min-w-0 w-fit max-w-[14rem] shrink tabular-nums">
                    <VersionSelect
                        // Named after the level: several of these sit side by side in one header row.
                        ariaLabel={translate('pages.job-architecture-v2.job-profile-version-for', { name: title })}
                        contentInset="none"
                        currentVersion={currentVersion}
                        disabled={versions.length < 2}
                        placeholder={
                            !is.undefined(selectedVersionId)
                                ? translate('pages.job-architecture-v2.job-profile-version', {
                                      version: selectedVersionId,
                                  })
                                : ''
                        }
                        versions={formattedVersions}
                        onSwitchVersion={onSelectVersion}
                    />
                </div>
                <div className="shrink-0">
                    {isFetching ? <Spinner className="mr-0 text-text-primary" /> : currentVersion?.badge}
                </div>
            </div>
        </div>
    );
};
